import { useCallback, useContext, useState, useMemo } from "react";
import {
  Group,
  Checkbox,
  Grid,
  Button,
  ActionIcon,
  Highlight,
} from "@mantine/core";
import { IconEye, IconFolder, IconFolderPlus } from "@tabler/icons-react";
import { FoldersContext } from "../contexts/FoldersContext";
import FolderTooltip from "./FolderTooltip";
import FolderMenu from "./FolderMenu";

const FolderSearchResultsRow = ({
  folder: node,
  enableSelect = false,
  enableView = false,
  selected,
  onSelect,
  showFolderActionMenu = true,
  showFolderCreateButton = true,
  disabledFolderActionMenuItems = [],
  searchQuery,
  onSearchClear,
  toggleFolderFilter,
  filterSet,
  enableFilters = false,
}) => {
  const {
    setFolderToModify,
    openCreate,
    canCreateFolder,
    setViewingFolder,
    sortedFolders: allFolders,
  } = useContext(FoldersContext);
  const [hovered, setHovered] = useState(false);

  const descendantLookup = useMemo(() => {
    const lookup = {};
    function traverse(folder) {
      let leafIds = [];
      if (folder.children && folder.children.length > 0) {
        folder.children.forEach((child) => {
          leafIds = leafIds.concat(traverse(child));
        });
      } else {
        leafIds = [String(folder.value)];
      }
      lookup[String(folder.value)] = {
        leafIds,
        totalLeafCount: leafIds.length,
      };
      return leafIds;
    }

    if (allFolders.length > 0) {
      allFolders.forEach((root) => traverse(root));
    } else {
      traverse(node);
    }
    return lookup;
  }, [allFolders, node]);

  const { leafIds, totalLeafCount } = descendantLookup[String(node.value)] || {
    leafIds: [String(node.value)],
    totalLeafCount: 1,
  };
  const checkedCount = enableFilters
    ? leafIds.filter((lid) => filterSet?.has(lid)).length
    : 0;
  const checked = totalLeafCount > 0 && checkedCount === totalLeafCount;
  const indeterminate = checkedCount > 0 && checkedCount < totalLeafCount;

  const handleCreateFolder = useCallback(
    (e, node) => {
      e.stopPropagation();
      setFolderToModify(node);
      openCreate();
    },
    [setFolderToModify, openCreate]
  );

  const handleSelect = useCallback(
    (e, folder) => {
      e.stopPropagation();
      onSelect(folder);
    },
    [onSelect]
  );

  const handleView = useCallback(
    (e, folder) => {
      e.stopPropagation();
      setViewingFolder(folder);
      onSearchClear();
    },
    [setViewingFolder]
  );

  const searchTerms = searchQuery
    ? searchQuery
        .trim()
        .split(/[\s\-_]+/)
        .filter((term) => term.length >= 2)
    : [];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        paddingRight: 12,
        gap: 4,
      }}
    >
      {!enableSelect && (
        <Checkbox.Indicator
          checked={checked}
          indeterminate={indeterminate}
          onClick={() => {
            console.log("Checkbox clicked");
            toggleFolderFilter(node?.value);
          }}
          data-testid={`folder-search-result-checkbox-${node.label
            .toLowerCase()
            .replace(/\s+/g, "-")}`}
        />
      )}
      <FolderTooltip folder={node} showPath>
        <div
          onClick={
            enableSelect
              ? (e) => handleSelect(e, node)
              : () => toggleFolderFilter(node?.value)
          }
          className="folder-node"
          data-selected={selected?.value === node?.value}
          style={{
            flex: 1,
            flexWrap: "nowrap",
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "4px 0px 4px 4px",
          }}
        >
          <IconFolder size={16} />
          <Grid flex={1} align="center" style={{ flexWrap: "nowrap" }}>
            <Grid.Col span="auto">
              <Highlight
                highlight={searchTerms}
                size="sm"
                lineClamp={1}
                style={{ wordBreak: "break-word", userSelect: "none" }}
              >
                {node.label}
              </Highlight>
            </Grid.Col>
            {hovered && (
              <Grid.Col span="content">
                <Group
                  gap={2}
                  wrap="nowrap"
                  onClick={(e) => e.stopPropagation()}
                  mr={4}
                >
                  {enableView && (
                    <Button
                      variant="subtle"
                      size="xs"
                      onClick={(e) => handleView(e, node)}
                      leftSection={<IconEye size={11} />}
                      styles={{
                        root: {
                          height: "21px",
                          padding: "4px 4px",
                          fontSize: "10px",
                        },
                        section: {
                          marginRight: 3,
                          marginLeft: 0,
                        },
                      }}
                    >
                      View
                    </Button>
                  )}
                  {showFolderCreateButton && canCreateFolder && (
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="gray"
                      onClick={(e) => handleCreateFolder(e, node)}
                    >
                      <IconFolderPlus size={16} />
                    </ActionIcon>
                  )}
                  {showFolderActionMenu && (
                    <FolderMenu
                      folder={node}
                      disabledItems={disabledFolderActionMenuItems}
                    />
                  )}
                </Group>
              </Grid.Col>
            )}
          </Grid>
        </div>
      </FolderTooltip>
    </div>
  );
};

export default FolderSearchResultsRow;
